import { Modal, Col, Row, Card, Button } from "react-bootstrap";
import { useRef, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getSocket } from '../../../../socket';
import { getTimerColors } from 'utils/timerColors';
import { getActivityTimingLimits } from 'utils/activityTiming';

import Role from './PathwayComponents/Role';
import Limits from './SharedComponents/Limits';
import Event from './PathwayComponents/Event';
import Presenter from './PathwayComponents/Presenter';
import ProjectSelector from './PathwayComponents/ProjectSelector';
import Bell from './PathwayComponents/Bell';
import Clock from './PathwayComponents/Clock';
import Timer from "./PathwayComponents/Timer";
import PathwayActivity from './PathwayActivity';
import { isOfficer as isOfficerAcl } from 'utils/acl';

const toDateKey = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    if (value.includes('/')) {
      const [day, month, year] = value.split('/');
      if (day && month && year) {
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toISOString().slice(0, 10);
};

export default function PathwayItem({ item, getMeeting, meeting, isPlaying, setIsPlaying, showClockText = true, timerDisplayEnabled = true, allowTimerControls = true, timerControlsTarget = null, remoteControlled = false, swipeHandlers, debateEditing = false, onDebateEditingChange }) {
  const [backgroundColor, setBackgroundColor] = useState("rgba(0, 0, 0, 0)");
  const [time, setTime] = useState(item?.timer?.time || 0);
  const isDebate = item?.activity?.type === 'debate';
  const debateLimits = getActivityTimingLimits(item?.activity);
  const timerItem = debateLimits ? { ...item, agendaEvent: { ...item.agendaEvent, ...debateLimits } } : item;
  const [segmentColors, setSegmentColors] = useState({ background: 'rgba(0, 0, 0, 0)', foreground: 'var(--foreground)' });
  const mainTimerColors = getTimerColors(time, timerItem?.agendaEvent, {
    background: 'rgba(0, 0, 0, 0)',
    foreground: 'var(--foreground)',
  });
  const timerColors = isDebate ? segmentColors : mainTimerColors;
  const [selectedPresenterId, setSelectedPresenterId] = useState(item?.assignedUser?.id || '');
  const [hasTimerStarted, setHasTimerStarted] = useState(false);
  const [showProjectSelector, setShowProjectSelector] = useState(true);
  const [projectSelectorResetSignal, setProjectSelectorResetSignal] = useState(0);
  const [closeEventEditorSignal, setCloseEventEditorSignal] = useState(0);
  const [currentAttemptMeta, setCurrentAttemptMeta] = useState(item?.pathwayAttempt || null);

  // console.log("Pathway item", item);

  const timerRef = useRef();
  const pendingAttemptRef = useRef(null);
  const projectSelectorRef = useRef(null);
  const attemptResolutionPendingRef = useRef(false);

  const { user: authUser } = useSelector(x => x.auth)
  const url = process.env.REACT_APP_URL
  const { meetingId } = useParams();
  const meetingSpecialAssignments = meeting?.meetingDetails?.specialAssignments || meeting?.specialAssignments || {}
  const authIdentityIds = [
    authUser?.user?.localUserId,
    authUser?.user?._id,
    authUser?.user?.id,
    authUser?.user?.tmAuthUserId,
  ]
    .map(value => String(value || '').trim())
    .filter(Boolean)
  const isTimerManager = Boolean(
    authIdentityIds.length
    && authIdentityIds.includes(String(meetingSpecialAssignments.timerManager || '').trim())
  )
  const isMeetingLocked = Boolean(meeting?.meetingDetails?.isLocked)
  const emitTimerState = (nextIsPlaying) => {
    getSocket().emit('meeting:timer-state', {
      meetingId: meeting?.meetingDetails?.id || meeting?._id || item?.meeting,
      agendaItemId: item?.agendaItemId,
      isPlaying: nextIsPlaying,
      time,
    });
  };
  const authAcl = String(authUser?.user?.acl || authUser?.acl || '').trim();
  const isOfficerUser = isOfficerAcl(authAcl);
  const isWebmaster = Boolean(authUser?.user?.isWebmaster || authUser?.isWebmaster);
  const currentIdentityIds = [
    authUser?.user?.id,
    authUser?.user?._id,
    authUser?.user?.tmAuthUserId,
    authUser?.user?.localUserId,
  ].map(value => String(value || '').trim()).filter(Boolean);
  const isSelectedSpeaker = Boolean(
    [item?.assignedUser?.id, item?.assignedUser?._id, item?.assignedUser?.tmAuthUserId, item?.assignedUser?.localUserId]
      .filter(Boolean).some(id => currentIdentityIds.includes(String(id)))
  );
  const canEditPathwayItem = Boolean(
    authUser?.token && (isOfficerUser || isWebmaster || isSelectedSpeaker || isTimerManager)
  );
  const canEditActivity = Boolean(authUser?.token && !remoteControlled && (isWebmaster || isSelectedSpeaker));

  // console.log("Pathway authUser", authUser);

  // Reset time and stop playing when agenda item changes
  useEffect(() => {
    setSelectedPresenterId(item?.assignedUser?.id || '');
    setHasTimerStarted(false);
    setShowProjectSelector(false);
    setCurrentAttemptMeta(item?.pathwayAttempt?.attemptId ? item.pathwayAttempt : null);
    pendingAttemptRef.current = null;
  }, [item, canEditPathwayItem]);

  useEffect(() => {
    setTime(item?.timer?.time || 0);
  }, [item?.agendaItemId, item?.timer?.time]);

  useEffect(() => {
    if (!selectedPresenterId || !meetingId || !url || !authUser?.token) {
      return undefined;
    }

    if (
      currentAttemptMeta?.attemptId &&
      String(currentAttemptMeta.userId || '') === String(selectedPresenterId)
    ) {
      return undefined;
    }

    let cancelled = false;

    const hydrateCurrentAttemptMeta = async () => {
      try {
        let meetingDate = item?.meetingDate || '';

        if (!meetingDate) {
          const meetingResponse = await fetch(`${url}/meeting/${meetingId}`);
          if (meetingResponse.ok) {
            const meetingData = await meetingResponse.json();
            meetingDate = meetingData?.meetingDetails?.meetingDate || '';
          }
        }

        const targetDateKey = toDateKey(meetingDate || new Date().toISOString());
        if (!targetDateKey) return;

        const pathwayResponse = await fetch(`${url}/userpathway/${selectedPresenterId}`, {
          headers: {
            Authorization: `Bearer ${authUser.token}`,
          },
        });

        if (!pathwayResponse.ok) return;

        const userPathways = await pathwayResponse.json();
        const pathwayList = Array.isArray(userPathways) ? userPathways : [];

        for (const userPathway of pathwayList) {
          const projectsResponse = await fetch(
            `${url}/user/${selectedPresenterId}/userPathway/${userPathway._id}/projects`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authUser.token}`,
              },
            }
          );

          if (!projectsResponse.ok) {
            continue;
          }

          const projects = await projectsResponse.json();
          const projectList = Array.isArray(projects) ? projects : [];

          for (const project of projectList) {
            const attempts = Array.isArray(project?.attempts) ? project.attempts : [];
            const matchingAttempt = attempts.find((attempt) => toDateKey(attempt?.scheduledDate) === targetDateKey);

            if (!matchingAttempt?._id) {
              continue;
            }

            if (cancelled) return;

            setCurrentAttemptMeta({
              userId: selectedPresenterId,
              userPathwayId: userPathway._id,
              projectId: project?.projectId,
              attemptId: matchingAttempt._id,
              removable: false,
              attemptTitle: matchingAttempt?.speechTitle || '',
            });
            return;
          }
        }
      } catch (err) {
        // no-op: attempt can still be created manually from project selector
      }
    };

    hydrateCurrentAttemptMeta();

    return () => {
      cancelled = true;
    };
  }, [selectedPresenterId, meetingId, url, authUser?.token, item?.meetingDate, currentAttemptMeta?.attemptId, currentAttemptMeta?.userId]);

  useEffect(() => {
    if (isPlaying) {
      setHasTimerStarted(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    const currentMeetingId = meeting?.meetingDetails?.id || meeting?._id || item?.meeting;
    const agendaItemId = String(item?.agendaItemId || '').trim();
    if (!currentMeetingId || !agendaItemId) return undefined;
    const socket = getSocket();
    const handleTimerTick = (payload) => {
      if (payload?.segmentId || String(payload?.meetingId || '') !== String(currentMeetingId) || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
    };
    const handleTimerState = (payload) => {
      if (payload?.segmentId || String(payload?.meetingId || '') !== String(currentMeetingId) || String(payload?.agendaItemId || '') !== agendaItemId) return;
      const nextTime = Number(payload?.time);
      if (Number.isFinite(nextTime)) setTime(Math.max(0, nextTime));
      if (typeof payload?.isPlaying === 'boolean') setIsPlaying(payload.isPlaying);
    };
    const handleTimerReset = (payload) => {
      const resetMeetingId = payload?.meetingId || payload?.detail?.meetingId || currentMeetingId;
      const resetAgendaItemId = payload?.agendaItemId || payload?.detail?.agendaItemId;
      if (!payload?.segmentId && !payload?.detail?.segmentId && String(resetMeetingId) === String(currentMeetingId) && String(resetAgendaItemId || '') === agendaItemId) {
        setIsPlaying(false);
        setTime(0);
      }
    };
    socket.on('meeting:timer-tick', handleTimerTick);
    socket.on('meeting:timer-state', handleTimerState);
    socket.on('meeting:timer-reset', handleTimerReset);
    window.addEventListener('meeting:timer-reset', handleTimerReset);
    socket.emit('meeting:join', String(currentMeetingId));
    return () => {
      socket.off('meeting:timer-tick', handleTimerTick);
      socket.off('meeting:timer-state', handleTimerState);
      socket.off('meeting:timer-reset', handleTimerReset);
      window.removeEventListener('meeting:timer-reset', handleTimerReset);
    };
  }, [item?.agendaItemId, item?.meeting, meeting, setIsPlaying]);

  useEffect(() => {
    if (!showProjectSelector || isDebate) return undefined;

    const handleClickOutsideProjectSelector = (event) => {
      if (!projectSelectorRef.current?.contains(event.target)) {
        setShowProjectSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutsideProjectSelector);

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideProjectSelector);
    };
  }, [showProjectSelector, isDebate]);

  // Ticks arrive from the server (single source of truth), so no local interval
  // increments/emits here — that would double-count seconds across browsers.

  useEffect(() => {
    const handleExternalTimerReset = (event) => {
      const resetAgendaItemId = String(event?.detail?.agendaItemId || '').trim();
      if (event?.detail?.segmentId) return;

      const currentAgendaItemId = String(item?.agendaItemId || '').trim();
      if (!resetAgendaItemId || resetAgendaItemId !== currentAgendaItemId) return;

      setIsPlaying(false);
      setTime(0);
      setBackgroundColor("rgba(0, 0, 0, 0)");
    };

    window.addEventListener('meeting:timer-reset', handleExternalTimerReset);
    return () => window.removeEventListener('meeting:timer-reset', handleExternalTimerReset);
  }, [item?.agendaItemId, setIsPlaying]);

  const clearAgendaItemTitle = async () => {
    if (!item?.agendaItemId || !authUser?.token) return;

    try {
      const response = await fetch(`${url}/agendaItem/${item.agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser.token}`,
        },
        body: JSON.stringify({ item_title: null, pathwayAttempt: null }),
      });

      if (!response.ok) {
        throw new Error('Unable to reset pathway title.');
      }

      item.item_title = '';
      item.pathwayAttempt = null;
      item.activity = null;
      setCloseEventEditorSignal(prev => prev + 1);
      await Promise.resolve(getMeeting());
    } catch (err) {
      toast.error(err.message || 'Failed to reset pathway title.', { position: 'bottom-left' });
    }
  };

  const handleAttemptCreated = async (attemptMeta) => {
    if (!canEditPathwayItem) return;

    setCurrentAttemptMeta(attemptMeta || null);
    pendingAttemptRef.current = attemptMeta?.removable ? attemptMeta : null;

    const speechName = String(attemptMeta?.attemptTitle || '').trim();
    const pathwayName = String(attemptMeta?.pathwayName || '').trim();
    const projectNumber = String(attemptMeta?.projectNumber || '').trim();
    const projectName = String(attemptMeta?.projectName || '').trim();
    const pathwayProjectLine = [
      pathwayName,
      projectNumber ? `#${projectNumber}` : '',
      projectName,
    ]
      .filter(Boolean)
      .join(' ')
      .trim();
    const title = [speechName, pathwayProjectLine].filter(Boolean).join('\n') || item?.item_title || '';
    const timingPayload = {
      green: String(attemptMeta?.green || '').trim(),
      amber: String(attemptMeta?.amber || '').trim(),
      red: String(attemptMeta?.red || '').trim(),
      total: String(attemptMeta?.total || '').trim(),
    };
    const hasTimingPayload = Boolean(
      timingPayload.green || timingPayload.amber || timingPayload.red || timingPayload.total
    );

    try {
      const pathwayAttempt = attemptMeta?.userId
        && attemptMeta?.userPathwayId
        && attemptMeta?.projectId
        && attemptMeta?.attemptId
        ? {
          userId: attemptMeta.userId,
          userPathwayId: attemptMeta.userPathwayId,
          projectId: attemptMeta.projectId,
          attemptId: attemptMeta.attemptId,
        }
        : null;
      const updateItemRes = await fetch(`${url}/agendaItem/${item?.agendaItemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authUser?.token || ''}`,
        },
        body: JSON.stringify({
          item_title: title,
          pathwayAttempt,
        }),
      });

      if (!updateItemRes.ok) {
        const itemErr = await updateItemRes.json();
        throw new Error(itemErr?.error || itemErr?.Error || 'Unable to update title.');
      }

      if (hasTimingPayload && item?.agendaEvent?._id) {
        const updateEventRes = await fetch(`${url}/agendaEvent/${item.agendaEvent._id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser?.token || ''}`,
          },
          body: JSON.stringify(timingPayload),
        });

        if (!updateEventRes.ok) {
          const eventErr = await updateEventRes.json();
          throw new Error(eventErr?.error || eventErr?.Error || 'Unable to update pathway timing limits.');
        }

        if (item?.agendaEvent) {
          item.agendaEvent.green = timingPayload.green || item.agendaEvent.green;
          item.agendaEvent.amber = timingPayload.amber || item.agendaEvent.amber;
          item.agendaEvent.red = timingPayload.red || item.agendaEvent.red;
          item.agendaEvent.total = timingPayload.total || item.agendaEvent.total;
        }
      }

      item.item_title = title;
      item.pathwayAttempt = pathwayAttempt;
      setShowProjectSelector(false);
      setCloseEventEditorSignal(prev => prev + 1);
    } catch (err) {
      toast.error(err.message || 'Failed to update title from selected attempt.', { position: 'bottom-left' });
    }
  };

  const handlePresenterSelected = async ({ presenterId }) => {
    if (!isOfficerUser) {
      return;
    }

    const previousPresenterId = selectedPresenterId;
    const pendingAttempt = pendingAttemptRef.current;
    setSelectedPresenterId(presenterId || '');
    const presenterChanged = Boolean(previousPresenterId) && previousPresenterId !== presenterId;

    if (presenterChanged) {
      setCurrentAttemptMeta(null);
      pendingAttemptRef.current = null;
      setProjectSelectorResetSignal(prev => prev + 1);
      setShowProjectSelector(true);
      await clearAgendaItemTitle();
    }

    if (!pendingAttempt || !presenterChanged || hasTimerStarted) {
      return;
    }

    try {
      const response = await fetch(
        `${url}/user/${pendingAttempt.userId}/userPathway/${pendingAttempt.userPathwayId}/project/${pendingAttempt.projectId}/attempt/${pendingAttempt.attemptId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authUser?.token || ''}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Unable to remove draft attempt after presenter change.');
      }

      pendingAttemptRef.current = null;
      setShowProjectSelector(true);
    } catch (err) {
      console.error(err);
    }
  };

  const handleAttemptDeleted = async () => {
    setCurrentAttemptMeta(null);
    pendingAttemptRef.current = null;
    setProjectSelectorResetSignal(prev => prev + 1);
    setShowProjectSelector(true);
    await clearAgendaItemTitle();
  };

  const handleTimerReset = () => {
    const socket = getSocket();
    socket.emit('meeting:feedback-reset', {
      meetingId,
      agendaItemId: item?.agendaItemId,
    });
    window.dispatchEvent(new CustomEvent('pathway-feedback-reset', {
      detail: {
        meetingId,
        agendaItemId: item?.agendaItemId,
      },
    }));
  };

  const handleTimerStop = useCallback(() => {
    if (!meetingId) return;
    const socket = getSocket();
    const stoppedAt = Date.now();
    const emitStop = () => {
      socket.emit('meeting:feedback-stop', {
        meetingId,
        agendaItemId: item?.agendaItemId,
        attemptId: currentAttemptMeta?.attemptId || null,
        stoppedAt,
      });
    };
    if (socket.connected) emitStop();
    else socket.once('connect', emitStop);
  }, [meetingId, item?.agendaItemId, currentAttemptMeta?.attemptId]);

  const handleTimerStart = useCallback(async () => {
    if (item?.activity?.type === 'debate') return true;
    if (currentAttemptMeta?.attemptId) return true;
    if (attemptResolutionPendingRef.current || !item?.agendaItemId) return false;

    attemptResolutionPendingRef.current = true;
    try {
      const response = await fetch(`${url}/agendaItem/${item.agendaItemId}/pathway-attempt/resolve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authUser?.token || ''}`,
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.attemptId) {
        throw new Error(payload?.error || 'Unable to resolve the speech attempt.');
      }

      setCurrentAttemptMeta(payload);
      item.pathwayAttempt = payload;
      return true;
    } catch (error) {
      toast.error(error.message || 'Unable to resolve the speech attempt.', { position: 'bottom-left' });
      return false;
    } finally {
      attemptResolutionPendingRef.current = false;
    }
  }, [authUser?.token, currentAttemptMeta?.attemptId, item, url]);

  return (
    <Modal.Body
      style={{
        backgroundImage: 'var(--modal-bg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
      }}
      className='timer-header'
      {...(swipeHandlers || {})}
    >
      <Card
        className={`text-center mt-0 mb-0 modal-event-card-shell${timerDisplayEnabled && timerColors.background === '#ffc107' ? ' timer-amber' : ''}`}
        aria-label={isDebate ? 'Debate segment display' : undefined}
        style={{
          width: '100%',
          maxWidth: '100%',
          minHeight: '100%',
          height: '100%',
          margin: 0,
          border: '1px solid rgba(128, 128, 128, 0.35)',
          borderRadius: '12px',
          backgroundColor: !timerDisplayEnabled || timerColors.background === 'rgba(0, 0, 0, 0)'
            ? 'rgba(128, 128, 128, 0.08)'
            : timerColors.background,
          backdropFilter: 'blur(4px)',
          boxShadow: 'none',
          color: timerDisplayEnabled ? timerColors.foreground : 'var(--foreground)'
        }}
      >
        <Card.Body style={{ padding: 0, height: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          <div className="next-meeting-content" style={{ height: '100%' }}>
            {!isDebate && <Role item={item} />}
            {!isDebate && (authUser || remoteControlled) &&
              <Limits item={item} canEdit={canEditPathwayItem} />
            }
            {(!isDebate || (debateEditing && canEditPathwayItem)) && <Event
              item={item}
              canEdit={canEditPathwayItem}
              onEventTextClick={() => canEditPathwayItem && setShowProjectSelector(true)}
              closeEditorSignal={closeEventEditorSignal}
            />}
            {(isDebate ? debateEditing && canEditPathwayItem : showProjectSelector) && (
              <div ref={projectSelectorRef}>
                <ProjectSelector
                  presenterId={selectedPresenterId}
                  item={item}
                  isPlaying={isPlaying}
                  canEdit={canEditPathwayItem}
                  onAttemptCreated={handleAttemptCreated}
                  onAttemptDeleted={handleAttemptDeleted}
                  resetSignal={projectSelectorResetSignal}
                  visible={isDebate ? debateEditing : showProjectSelector}
                />
                {isDebate && <Button size="sm" variant="outline-secondary" onClick={() => onDebateEditingChange?.(false)}>Done editing debate</Button>}
              </div>
            )}
            {(!isDebate || (debateEditing && canEditPathwayItem)) && <Row className="justify-content-center align-items-center ">
              <Presenter
                item={item}
                getMeeting={getMeeting}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
                time={time}
                setTime={setTime}
                handleStop={typeof timerRef.current?.handleStop === "function" ? timerRef.current.handleStop : undefined}
                onPresenterSelected={handlePresenterSelected}
                isLocked={isMeetingLocked}
              />
              {!isDebate && (authUser || remoteControlled) && showClockText && timerDisplayEnabled &&
                <Col>
                  <Row>
                    {showClockText && <Clock time={time} isPlaying={isPlaying} />}
                    <Bell backgroundColor={backgroundColor} />

                  </Row>
                </Col>
              }
            </Row>}
            {(authUser || remoteControlled) && showClockText && timerDisplayEnabled &&
              <div hidden={isDebate && debateEditing && canEditPathwayItem}>
              <PathwayActivity
                item={item}
                meeting={meeting}
                getMeeting={getMeeting}
                canEdit={canEditActivity}
                canManageAssignments={canEditPathwayItem}
                isTimerManager={isTimerManager}
                isLocked={isMeetingLocked}
                onSegmentColorsChange={setSegmentColors}
                onFirstSegmentStart={() => {
                  if (isTimerManager && !isMeetingLocked) return timerRef.current?.handleStart();
                }}
                onLastSegmentStop={() => {
                  if (isTimerManager && !isMeetingLocked && isPlaying) return timerRef.current?.handleStop();
                }}
              />
              </div>
            }
            {authUser && isTimerManager && allowTimerControls &&
              <Timer
                ref={timerRef}
                controlsTarget={timerControlsTarget}
                item={timerItem}
                setBackgroundColor={setBackgroundColor}
                setIsPlaying={setIsPlaying}
                isPlaying={isPlaying}
                getMeeting={getMeeting}
                setTime={setTime}
                time={time}
                onStart={handleTimerStart}
                onReset={handleTimerReset}
                onStop={handleTimerStop}
                isTimerManager={isTimerManager}
                onTimerStateChange={emitTimerState}
                isLocked={isMeetingLocked}
              />
            }
          </div>
        </Card.Body>
      </Card>
    </Modal.Body>
  );
}
